import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { IssueCard, IssuesBlock } from '@/cloud/models/assistant.models';
import { issueRepoSegment } from '@/cloud/models/github.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';
import { timeAgo } from '../assistant-format';

/**
 * GitHub issue cards that stay current: when an issue is commented on, edited or closed through the dashboard
 * (`issues:changed`), its card is read again from GitHub.
 */
@Component({
    selector: 'app-assistant-issue-cards',
    standalone: true,
    imports: [TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (block().title) {
            <div class="ab-title">{{ block().title }}</div>
        }
        <div class="device-grid">
            @for (i of issues(); track i.repository + i.number) {
                <article class="device-card issue-row" [attr.data-state]="i.state" [class.flash]="flashed() === key(i)">
                    <header>
                        <i [class]="i.state === 'open' ? 'pi pi-circle state-open' : 'pi pi-check-circle state-closed'" aria-hidden="true"></i>
                        <a class="entity-link" [href]="i.htmlUrl" target="_blank" rel="noopener" [pTooltip]="i.title" tooltipPosition="top">#{{ i.number }} {{ i.title }}</a>
                        <span class="state" [attr.data-status]="i.state === 'open' ? 'online' : 'closed'">{{ i.state === 'open' ? t()('assistant.issues.open') : t()('assistant.issues.closed') }}</span>
                    </header>
                    <div class="meta">
                        <span><i class="pi pi-github" aria-hidden="true"></i>{{ i.packageName || i.repository }}</span>
                        @if (i.roomPath) {
                            <span><i class="pi pi-map-marker" aria-hidden="true"></i>{{ i.roomPath }}</span>
                        }
                        <span><i class="pi pi-comments" aria-hidden="true"></i>{{ fill(t()('assistant.issues.comments'), { count: i.commentsCount }) }}</span>
                        <span><i class="pi pi-clock" aria-hidden="true"></i>{{ updated(i) }}</span>
                        @if (i.assignees.length) {
                            <span><i class="pi pi-user" aria-hidden="true"></i>{{ i.assignees.join(', ') }}</span>
                        }
                    </div>
                    @if (visibleLabels(i).length || i.problem) {
                        <div class="issue-labels">
                            @if (i.problem) {
                                <span class="label-chip locked"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ t()('assistant.issues.problem') }}</span>
                            }
                            @for (l of visibleLabels(i); track l.name) {
                                <span class="label-chip" [style.--label-color]="'#' + (l.color || '8b949e')">{{ l.name }}</span>
                            }
                        </div>
                    }
                    <footer>
                        @if (!serviceDesk()) {
                            <button type="button" class="quick" (click)="open(i)"><i class="pi pi-github" aria-hidden="true"></i>{{ t()('assistant.issue.openPortal') }}</button>
                        }
                        <button type="button" class="quick" (click)="ask(i)"><i class="pi pi-comment" aria-hidden="true"></i>{{ t()('assistant.issues.ask') }}</button>
                    </footer>
                </article>
            }
        </div>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantIssueCardsComponent {
    readonly block = input.required<IssuesBlock>();

    private api = inject(CloudApiService);
    private assistant = inject(AssistantService);
    private tenants = inject(TenantService);
    private router = inject(Router);
    readonly t = inject(TranslationService).t;
    readonly fill = fill;

    private readonly live = signal<Record<string, Partial<IssueCard>>>({});
    private readonly now = signal(Date.now());
    readonly flashed = signal<string | null>(null);
    readonly serviceDesk = computed(() => this.assistant.scope() === 'serviceDesk');

    readonly issues = computed(() => this.block().issues.map((i) => ({ ...i, ...(this.live()[this.key(i)] ?? {}) }) as IssueCard));

    constructor() {
        const destroyRef = inject(DestroyRef);
        this.api.issueEvents$
            .pipe(
                filter((e) => this.block().issues.some((i) => i.number === e.number && i.repository.toLowerCase() === e.repository.toLowerCase())),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe((e) => this.refresh(e.repository, e.number));
        const timer = setInterval(() => this.now.set(Date.now()), 60_000);
        destroyRef.onDestroy(() => clearInterval(timer));
    }

    key(i: Pick<IssueCard, 'repository' | 'number'>): string {
        return `${i.repository.toLowerCase()}#${i.number}`;
    }

    /** Labels people read; the API's own room and problem labels are summarised instead. */
    visibleLabels(i: IssueCard): IssueCard['labels'] {
        return i.labels.filter((l) => !l.name.startsWith('proav:'));
    }

    updated(i: IssueCard): string {
        const time = timeAgo(i.updatedUtc, this.now());
        return time ? fill(this.t()('assistant.issues.updated'), { time }) : '';
    }

    open(i: IssueCard): void {
        const ns = this.block().namespaceId;
        if (ns && ns !== this.tenants.selectedTenantId()) this.tenants.select(ns);
        this.router.navigate(['/issues', issueRepoSegment(i.repository), i.number]);
    }

    ask(i: IssueCard): void {
        // Only the reference: the title is GitHub text, so the assistant reads it through its issue tool as data.
        this.assistant.send(`What is the latest on ${i.repository}#${i.number}? Is the problem still happening?`);
    }

    private async refresh(repository: string, number: number): Promise<void> {
        try {
            const detail = await this.api.getIssue(this.block().namespaceId, repository, number);
            const k = this.key({ repository, number });
            this.live.update((map) => ({
                ...map,
                [k]: {
                    title: detail.issue.title,
                    state: detail.issue.state,
                    stateReason: detail.issue.stateReason,
                    commentsCount: detail.issue.commentsCount,
                    updatedUtc: detail.issue.updatedUtc,
                    assignees: detail.issue.assignees,
                    labels: detail.issue.labels.map((l) => ({ name: l.name, color: l.color }))
                }
            }));
            this.flashed.set(k);
            setTimeout(() => this.flashed.update((f) => (f === k ? null : f)), 1500);
        } catch {
            // The card keeps what it showed; the issue page has the full story.
        }
    }
}
